import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (token) => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser({ ...res.data, token });
    } catch {
      localStorage.removeItem("app_token");
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("app_token");
    if (token) {
      fetchMe(token);
    } else {
      setUser(false);
      setLoading(false);
    }
  }, [fetchMe]);

  const loginWithLevelUp = async (levelupToken) => {
    const res = await axios.post(`${API}/auth/levelup`, { token: levelupToken });
    const { token, user: userData } = res.data;
    localStorage.setItem("app_token", token);
    // Clear any stale local-admin session so the two auth systems don't bleed into each other
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_name");
    localStorage.removeItem("admin_role");
    localStorage.removeItem("admin_permissions");
    // Elevated SSO users (e.g. admin, super_admin, microsoft_manager) must be able
    // to reach the /admin dashboard. Mirror their role + permissions into admin_* keys
    // AND set admin_token so all admin dashboard API calls work (many read admin_token directly).
    const perms = userData?.permissions || [];
    if (userData?.role && userData.role !== "user" && perms.length > 0) {
      localStorage.setItem("admin_token", token);
      localStorage.setItem("admin_role", userData.role);
      localStorage.setItem("admin_name", userData.first_name || userData.email || "Manager");
      localStorage.setItem("admin_permissions", JSON.stringify(perms));
    }
    setUser({ ...userData, token });
    return userData;
  };

  /** Called when a local "user" role admin logs in — hydrates AuthContext without an API round-trip. */
  const loginWithLocalUser = (userData, appToken) => {
    localStorage.setItem("app_token", appToken);
    setUser({ ...userData, token: appToken });
  };

  const logout = () => {
    localStorage.removeItem("app_token");
    setUser(false);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user && user !== false,
    loginWithLevelUp,
    loginWithLocalUser,
    logout,
    token: user?.token || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
